import { useState } from 'react';
import { activities, activityCategories } from '../../../mocks/activities';

export default function ActivitiesGrid() {
  const [selectedCategory, setSelectedCategory] = useState('All Activities');
  const [expandedActivity, setExpandedActivity] = useState<number | null>(null);

  const filteredActivities = selectedCategory === 'All Activities'
    ? activities
    : activities.filter(activity => activity.category === selectedCategory);

  return (
    <section id="activities" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Activities & Recreation
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover endless ways to play, explore, and create memories in Roseville
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {activityCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap ${
                selectedCategory === category
                  ? 'bg-teal-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={activity.image}
                  alt={activity.name}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                    {activity.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {activity.name}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {activity.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-map-pin-line text-teal-500 mt-0.5"></i>
                    <span>{activity.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-time-line text-teal-500"></i>
                    <span>{activity.hours}</span>
                  </div>
                </div>

                {/* Amenities */}
                {expandedActivity === activity.id ? (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Amenities:</h4>
                    <div className="flex flex-wrap gap-2">
                      {activity.amenities.map((amenity, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-3">
                  <button
                    onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    {expandedActivity === activity.id ? 'Show Less' : 'View Details'}
                  </button>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i>
                    Directions
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-community-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">75+</div>
            <div className="text-sm text-gray-600">Parks &amp; Trails</div>
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries/parks_trails"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 mt-2 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              City of Roseville
            </a>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-basketball-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">50+</div>
            <div className="text-sm text-gray-600">Sports Facilities</div>
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries/recreation_programs/sports"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 mt-2 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Roseville Recreation
            </a>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-gamepad-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">25+</div>
            <div className="text-sm text-gray-600">Entertainment Venues</div>
            <a
              href="https://www.visitroseville.com/things-to-do/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 mt-2 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Visit Roseville
            </a>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-event-line text-teal-600 text-2xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">200+</div>
            <div className="text-sm text-gray-600">Annual Events</div>
            <a
              href="https://www.roseville.ca.us/government/departments/parks_recreation_libraries/recreation_programs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 mt-2 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Roseville Programs
            </a>
          </div>
        </div>

        {/* Sources footnote */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Sources: City of Roseville Parks &amp; Recreation Department, Visit Roseville. Data as of 2025.
          </p>
        </div>
      </div>
    </section>
  );
}